import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExpectedKpiListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/expected-kpi-list/expected-kpi-list.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ICampaignExportApi } from './campaign-export-dialog.interface';
import { CampaignExportOrdersService } from './campaign-export-orders.service';
import { CampaignExportService } from './campaign-export.service';

interface ICampaignExportDialogState {
  campaignExports: ICampaignExportApi[];
  platformFeaturesKpis: TExpectedKpi[] | null;
  loading: boolean;
  loadingPlatformFeatureKpis: boolean;
  creatingExport: boolean;
  deletingExport: boolean;
  savingExport: boolean;
  error: string | null;
  lastCreatedCampaignExportId: string | null;
}

const initialState: ICampaignExportDialogState = {
  campaignExports: [],
  creatingExport: false,
  deletingExport: false,
  error: null,
  lastCreatedCampaignExportId: null,
  loading: false,
  loadingPlatformFeatureKpis: false,
  platformFeaturesKpis: null,
  savingExport: false,
};

function sortByOrder(campaignExports: ICampaignExportApi[]): ICampaignExportApi[] {
  return [...campaignExports].sort((a, b) => (a.order?.order ?? 0) - (b.order?.order ?? 0));
}

@Injectable()
export class CampaignExportDialogStore extends ComponentStore<ICampaignExportDialogState> {
  public readonly campaignExports$ = this.selectSignal((state: ICampaignExportDialogState) => state.campaignExports);
  public readonly platformFeaturesKpis$ = this.selectSignal(
    (state: ICampaignExportDialogState) => state.platformFeaturesKpis,
  );
  public readonly loading$ = this.selectSignal((state: ICampaignExportDialogState) => state.loading);
  public readonly creatingExport$ = this.selectSignal((state: ICampaignExportDialogState) => state.creatingExport);
  public readonly error$ = this.selectSignal((state: ICampaignExportDialogState) => state.error);
  public readonly lastCreatedCampaignExportId$ = this.selectSignal(
    (state: ICampaignExportDialogState) => state.lastCreatedCampaignExportId,
  );

  public readonly busy$ = this.selectSignal(
    (state: ICampaignExportDialogState) => state.creatingExport || state.deletingExport || state.savingExport,
  );

  /** Stashed while an optimistically-removed report's delete request is in flight, so a failed
   *  request can restore it to its original position. Only one delete can be in flight at a time
   *  (gated by `busy$` elsewhere), so a single field is enough — no need for a map/queue. */
  private pendingDeletedCampaignExport: { campaignExport: ICampaignExportApi; index: number } | null = null;

  /** Snapshot of the list right before an optimistic drag reorder, so a failed order PUT can
   *  restore exactly what the user saw before dragging. */
  private preReorderCampaignExports: ICampaignExportApi[] | null = null;

  /** Set by the component call site right before invoking `createCampaignExport`, so the shared
   *  effect below can tell a fresh "Add Report" apart from a "Clone" for analytics purposes —
   *  both funnel through this same store method. Read once and cleared by the consumer. */
  public pendingReportCreationSource: 'create' | 'clone' | null = null;

  public readonly fetchCampaignExports = this.effect((trigger$: Observable<{ campaignId: string }>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(({ campaignId }) => {
        const params = new HttpParams().set(
          'joins',
          [
            'campaign_exports',
            'campaign_exports.order',
            'campaign_exports.creators',
            'campaign_exports.platform_features',
            'campaign_exports.kpis',
            'campaign_exports.tabs',
            'campaign_exports.content_matching_types',
            'campaign_exports.deliverable_statuses',
            'campaign_exports.kpi_statuses',
            'campaign_exports.observation_statuses',
            'campaign_exports.content_statuses',
          ].join(','),
        );

        return this.campaignsService.getCampaign(campaignId, params).pipe(
          tap((campaign) => {
            this.patchState({ campaignExports: sortByOrder(campaign?.campaignExports ?? []), loading: false });
          }),
          catchError(() => {
            this.patchState({
              error: 'pages.campaignDetails.export.loadError',
              loading: false,
            });

            return EMPTY;
          }),
        );
      }),
    );
  });

  public readonly createCampaignExport = this.effect((trigger$: Observable<ICampaignExportApi>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ creatingExport: true, error: null })),
      switchMap((campaignExportPayload) =>
        // The backend already assigns the new report its order (appended last) and returns it embedded.
        this.campaignExportService.createCampaignExport(campaignExportPayload).pipe(
          tap((createdCampaignExport) => {
            this.patchState({
              campaignExports: sortByOrder([...this.get().campaignExports, createdCampaignExport]),
              creatingExport: false,
              lastCreatedCampaignExportId: createdCampaignExport.id,
            });
          }),
          catchError(() => {
            this.patchState({
              creatingExport: false,
              error: 'pages.campaignDetails.export.createError',
            });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly deleteCampaignExport = this.effect((trigger$: Observable<{ id: string; onDeleted?: () => void }>) => {
    return trigger$.pipe(
      tap(({ id }) => {
        // Optimistically remove it right away for a responsive UI; stash its position so a
        // failed request can put it back where it was.
        const { campaignExports } = this.get();
        const index = campaignExports.findIndex((campaignExport) => campaignExport.id === id);
        this.pendingDeletedCampaignExport = index === -1 ? null : { campaignExport: campaignExports[index], index };

        this.patchState({
          campaignExports: campaignExports.filter((campaignExport) => campaignExport.id !== id),
          deletingExport: true,
          error: null,
        });
      }),
      switchMap(({ id, onDeleted }) =>
        this.campaignExportService.deleteCampaignExport(id).pipe(
          tap(() => {
            this.pendingDeletedCampaignExport = null;
            this.patchState({ deletingExport: false });
            // Only fire once the delete is actually confirmed - the removal above is optimistic
            // and gets reverted in catchError below on failure.
            onDeleted?.();
          }),
          catchError(() => {
            if (this.pendingDeletedCampaignExport) {
              const { campaignExport, index } = this.pendingDeletedCampaignExport;
              const campaignExports = [...this.get().campaignExports];
              campaignExports.splice(index, 0, campaignExport);
              this.patchState({ campaignExports });
              this.pendingDeletedCampaignExport = null;
            }

            this.patchState({
              deletingExport: false,
              error: 'pages.campaignDetails.export.deleteError',
            });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly updateCampaignExportOrder = this.effect(
    (
      trigger$: Observable<{
        campaignExports: ICampaignExportApi[];
        orderId: string;
        order: number;
        campaignExportId: string;
        campaignId: string;
      }>,
    ) => {
      return trigger$.pipe(
        tap(({ campaignExports }) => {
          this.preReorderCampaignExports = this.get().campaignExports;
          this.patchState({ campaignExports, error: null });
        }),
        switchMap(({ orderId, order, campaignExportId, campaignId }) =>
          this.campaignExportOrdersService
            .updateCampaignExportOrder({ campaignExportId, campaignId, id: orderId, order })
            .pipe(
              tap(() => {
                this.preReorderCampaignExports = null;
              }),
              catchError(() => {
                if (this.preReorderCampaignExports) {
                  this.patchState({ campaignExports: this.preReorderCampaignExports });
                  this.preReorderCampaignExports = null;
                }

                this.patchState({ error: 'pages.campaignDetails.export.orderUpdateError' });

                return EMPTY;
              }),
            ),
        ),
      );
    },
  );

  public readonly fetchPlatformFeatureKpis = this.effect((trigger$: Observable<EPlatformFeature[]>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loadingPlatformFeatureKpis: true })),
      switchMap((platformFeatures: EPlatformFeature[]) => {
        return this.expectedKpiListService.getPlatformFeaturesKpisPerformance(platformFeatures).pipe(
          tap((platformFeaturesKpis: TExpectedKpi[]) => {
            this.patchState({ loadingPlatformFeatureKpis: false, platformFeaturesKpis });
          }),
          catchError(() => {
            this.patchState({
              error: 'pages.campaignDetails.export.platformFeatureKpisError',
              loadingPlatformFeatureKpis: false,
            });

            return EMPTY;
          }),
        );
      }),
    );
  });

  /** Optimistic local reorder from drag-and-drop — intentionally NOT re-sorted by order.order,
   *  since the caller already computed the new authoritative order. */
  public setCampaignExports(campaignExports: ICampaignExportApi[]): void {
    this.patchState({ campaignExports });
  }

  public updateCampaignExportInList(campaignExport: ICampaignExportApi): void {
    this.patchState({
      campaignExports: this.get().campaignExports.map((existing) =>
        existing.id === campaignExport.id ? campaignExport : existing,
      ),
    });
  }

  public setSavingExport(savingExport: boolean): void {
    this.patchState({ savingExport });
  }

  public setCreatingExport(creatingExport: boolean): void {
    this.patchState({ creatingExport });
  }

  constructor(
    private readonly campaignExportService: CampaignExportService,
    private readonly campaignExportOrdersService: CampaignExportOrdersService,
    private readonly campaignsService: CampaignsService,
    private readonly expectedKpiListService: ExpectedKpiListService,
  ) {
    super(initialState);
  }
}
